import {
  FulfillmentTypeListResponseType,
  SupplierGetResponseType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import { DeliveryOrderItemType } from "./CourierDeliveryOptionsCard";
import { DeliveryOptionsCardCompact } from "./DeliveryOptionsCardCompact";
import { Button, Icon } from "@sellar-io/components";
import { Disclosure, DisclosureButton } from "@headlessui/react";
import { useState } from "react";
import { DistributionEducationDialog } from "../../pages/discovery/v2/distribution/DistributionEducationDialog";
import { DistributionEducationDialogPage } from "../../pages/discovery/v2/distribution/DistributionEducationDialog.shared";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { twMerge } from "tailwind-merge";
import dayjs from "dayjs";
import { useDistributionDeliveryEstimate } from "~/hooks/useDeliveryEstimates/useDistributionDeliveryEstimate";
import { SlideTransition } from "../transition/SlideTransition";

export const DistributionDeliveryOptionsCard = ({
  deliveryArea,
  supplier,
  directFulfillmentType,
  orderItems,
  buyerPostcode,
}: {
  deliveryArea?: SupplierStorefrontV2GetResponseType["sdDeliveryArea"];
  supplier: SupplierGetResponseType;
  directFulfillmentType:
    | Omit<
        FulfillmentTypeListResponseType[number],
        | "id"
        | "name"
        | "OrderRules"
        | "numberOfCustomers"
        | "numberOfZones"
        | "DeliveryMethodContainerRules"
      >
    | undefined;
  orderItems?: DeliveryOrderItemType;
  buyerPostcode: string;
}) => {
  return (
    <>
      <DeliveryOptionsCardCompact
        supplierId={supplier.id}
        description={
          deliveryArea ? (
            <DistributionDeliveryContentCompact
              supplier={supplier}
              orderItems={orderItems}
              buyerPostcode={buyerPostcode}
            />
          ) : null
        }
        fulfillmentType={directFulfillmentType}
        title={
          <div className="flex items-center gap-1">
            <span>
              Delivered by{" "}
              <span className={twMerge("font-semibold text-[#8C23C7]")}>
                Sellar
              </span>
            </span>
          </div>
        }
      >
        <DistributionEducationDeliveryContent
          buyerPostcode={buyerPostcode}
          supplierId={supplier.id}
        />
      </DeliveryOptionsCardCompact>
    </>
  );
};

const DistributionDeliveryContentCompact = ({
  supplier,
  orderItems,
  buyerPostcode,
}: {
  supplier: SupplierGetResponseType;
  orderItems?: DeliveryOrderItemType;
  buyerPostcode: string;
}) => {
  const productIds = orderItems
    ? orderItems.map((item) => item.Product.id)
    : [];

  const { nextDeliveryDate, orderByDate } = useDistributionDeliveryEstimate({
    supplierId: supplier.id,
    productIds,
    buyerPostcode,
  });

  if (!nextDeliveryDate) return null;
  return (
    <div className={"flex flex-col"}>
      <p>
        Get it by <strong>{dayjs(nextDeliveryDate).format("ddd D MMM")}</strong>
      </p>
      {orderByDate ? (
        <p>
          Order by{" "}
          <span className={"underline"}>
            {dayjs(orderByDate).format("HH:mm")}
          </span>{" "}
          on{" "}
          <span className={"underline"}>
            {dayjs(orderByDate).format("ddd D MMM")}
          </span>
        </p>
      ) : null}
    </div>
  );
};

const DistributionEducationDeliveryContent = ({
  buyerPostcode,
  supplierId,
}: {
  buyerPostcode: string;
  supplierId: number;
}) => {
  const [openDistributionEducationDialog, setOpenDistributionEducationDialog] =
    useState(false);
  const { business } = useSellarCurrentBusiness();
  const isBuyer = !!business?.Retailer;

  return (
    <>
      <Disclosure as="div" className="flex w-full flex-1 flex-col">
        {({ open }) => (
          <>
            <DisclosureButton
              type="button"
              className="flex w-full cursor-pointer items-center gap-2 border-t border-[#D1A7E9] py-2 text-sm text-[#8C23C7]"
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <span>
                <Icon size={0.7} icon="INFORMATION_OUTLINE" />
              </span>
              <span className="flex items-center gap-2 font-semibold">
                What is this?
                {open ? (
                  <Icon icon="CHEVRON_UP" />
                ) : (
                  <Icon icon="CHEVRON_DOWN" />
                )}
              </span>
            </DisclosureButton>
            <SlideTransition show={open} parentClass="duration-200 ease-out">
              <div className="flex flex-col gap-2 rounded-lg border border-[#E8D3F4] bg-[#E8D3F44D] p-3">
                <span className="text-sm font-semibold">
                  Delivered by Sellar
                </span>
                <ul className="list-inside list-disc text-xs text-zinc-600">
                  <li>
                    Flexible, expert dray delivery - no couriers or pallets
                  </li>
                  <li>Free of charge with no minimums</li>
                  <li>Get orders from multiple suppliers delivered together</li>
                </ul>
                {isBuyer && (
                  <Button
                    onClick={() => {
                      setOpenDistributionEducationDialog(true);
                    }}
                    color="slim"
                  >
                    Set delivery preferences
                  </Button>
                )}
              </div>
            </SlideTransition>
          </>
        )}
      </Disclosure>
      <DistributionEducationDialog
        active={openDistributionEducationDialog}
        onDismiss={() => {
          setOpenDistributionEducationDialog(false);
        }}
        initialPage={DistributionEducationDialogPage.SET_DELIVERY_PREFERENCES}
        buyerPostcode={buyerPostcode}
        supplierId={supplierId}
      />
    </>
  );
};
